import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Award, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Landing page after clicking "Claim on CYQR.CA" from an embed widget.
 * Auto-awards the badge once the user is logged in.
 */
const ClaimBadge = () => {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [badge, setBadge] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [message, setMessage] = useState('');

  // Fetch badge info on mount
  useEffect(() => {
    api.get(`/badges/public/${code}`)
      .then(res => setBadge(res.data))
      .catch(() => setStatus('error'));
  }, [code]);

  // Once user is available, auto-claim
  useEffect(() => {
    if (!user || !badge || status !== 'idle') return;
    setStatus('loading');
    api.post(`/badges/claim-secret/${code}`)
      .then(res => {
        if (res.data.already_claimed) {
          setStatus('already');
          setMessage(res.data.message);
        } else {
          setStatus('success');
          setMessage(res.data.message);
        }
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Something went wrong.');
      });
  }, [user, badge, code, status]);

  // Not logged in → redirect to login, come back here after
  const handleLogin = () => {
    sessionStorage.setItem('redirectAfterLogin', `/claim-badge/${code}`);
    navigate('/login');
  };

  const handleRegister = () => {
    sessionStorage.setItem('redirectAfterLogin', `/claim-badge/${code}`);
    navigate('/register');
  };

  const iconIsUrl = badge?.icon_url && badge.icon_url.startsWith('http');

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full">

          {/* Badge preview card */}
          {badge && (
            <div className="bg-[#111] border border-[#C5A52A]/30 rounded-lg p-8 mb-6 text-center">
              <div className="mb-4">
                {iconIsUrl
                  ? <img src={badge.icon_url} alt={badge.name} className="w-20 h-20 mx-auto object-contain" />
                  : <span className="text-6xl">{badge.icon_url || '🏅'}</span>
                }
              </div>
              <p className="font-mono text-xs text-[#C5A52A] tracking-widest mb-1">BADGE</p>
              <h1 className="font-display text-2xl text-white mb-2">{badge.name}</h1>
              {badge.description && (
                <p className="text-gray-400 text-sm">{badge.description}</p>
              )}
              {badge.reward_points > 0 && (
                <p className="text-[#22C55E] font-mono text-sm mt-2">+{badge.reward_points} points</p>
              )}
            </div>
          )}

          {/* State: not logged in */}
          {!user && status !== 'error' && (
            <div className="text-center">
              <p className="text-gray-400 mb-6 text-sm">
                Log in or create a free CYQR.CA account to claim this badge.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleLogin}
                  data-testid="claim-login-btn"
                  className="px-6 py-2 bg-[#C5A52A] text-black font-bold rounded hover:bg-[#d4b43a] transition-colors"
                >
                  Log In & Claim
                </button>
                <button
                  onClick={handleRegister}
                  data-testid="claim-register-btn"
                  className="px-6 py-2 border border-[#C5A52A]/50 text-[#C5A52A] font-bold rounded hover:border-[#C5A52A] transition-colors"
                >
                  Sign Up Free
                </button>
              </div>
            </div>
          )}

          {/* State: claiming */}
          {user && status === 'loading' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#C5A52A] mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Claiming your badge...</p>
            </div>
          )}

          {/* State: success */}
          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-white font-bold text-lg mb-1">Badge Claimed!</p>
              <p className="text-gray-400 text-sm mb-6">{message}</p>
              <button
                onClick={() => navigate('/badges')}
                className="px-6 py-2 bg-[#C5A52A] text-black font-bold rounded hover:bg-[#d4b43a] transition-colors"
              >
                View My Badges
              </button>
            </div>
          )}

          {/* State: already claimed */}
          {status === 'already' && (
            <div className="text-center">
              <Award className="w-12 h-12 text-[#C5A52A] mx-auto mb-3" />
              <p className="text-white font-bold text-lg mb-1">Already Yours!</p>
              <p className="text-gray-400 text-sm mb-6">{message}</p>
              <button
                onClick={() => navigate('/badges')}
                className="px-6 py-2 border border-[#C5A52A]/50 text-[#C5A52A] font-bold rounded hover:border-[#C5A52A] transition-colors"
              >
                View My Badges
              </button>
            </div>
          )}

          {/* State: error */}
          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-white font-bold text-lg mb-1">Something went wrong</p>
              <p className="text-gray-400 text-sm">{message || 'Badge not found or unavailable.'}</p>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default ClaimBadge;
